import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoEyeOutline,
  IoCreateOutline,
  IoTrashOutline,
  IoPrintOutline,
  IoSendOutline,
  IoCheckmarkOutline,
  IoCloseOutline,
} from "react-icons/io5";
import arService from "../../../../../services/arService";
import { hasPermission } from "../../../../../utils/permission";
import "./DebitNoteList.css";
import { showMessageBox } from '../../../../site/fn';
import { EmptyState, ErrorState } from '../../../../../shared-ui/States';

const DebitNoteList = () => {
  const [debitNotes, setDebitNotes] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const canEdit = hasPermission("ar-debit-note", "set");
  const canDelete = hasPermission("ar-debit-note", "delete");
  const canDeleteDocument = (doc) =>
    doc.approval_status !== "Approved" && !doc.is_deleted;
  const canApprove = hasPermission("ar-debit-note", "approve");

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹ 0.00";
    return `₹ ${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusBadge = (status, approvalStatus, isDraft) => {
    if (isDraft) {
      return <span className="status-badge draft">Draft</span>;
    }
    if (approvalStatus === "Approved") {
      return <span className="status-badge approved">Approved</span>;
    }
    if (approvalStatus === "Rejected") {
      return <span className="status-badge rejected">Rejected</span>;
    }
    if (approvalStatus === "Pending Approval" || approvalStatus === "Pending") {
      return <span className="status-badge pending">Pending Approval</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  useEffect(() => {
    fetchDebitNotes();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredData(debitNotes);
    } else {
      const filtered = debitNotes.filter((dn) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (dn.doc_no && dn.doc_no.toLowerCase().includes(searchLower)) ||
          (dn.customer_name && dn.customer_name.toLowerCase().includes(searchLower)) ||
          (dn.site_name && dn.site_name.toLowerCase().includes(searchLower)) ||
          (dn.reason && dn.reason.toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, debitNotes]);

  const fetchDebitNotes = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const data = await arService.getDebitNotes();
      const sorted = [...data].sort((a, b) => {
        const idDiff = Number(b.id || 0) - Number(a.id || 0);
        if (idDiff !== 0) return idDiff;
        return new Date(b.debit_note_date || 0) - new Date(a.debit_note_date || 0);
      });
      setDebitNotes(sorted);
      setFilteredData(sorted);
    } catch (error) {
      console.error("Error fetching debit notes:", error);
      setLoadError(error.message || "Failed to fetch debit notes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (id) => {
    navigate(`/ar/debit-notes/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/ar/debit-notes/${id}/edit`);
  };

  const handleDelete = async (id, docNo) => {
    const reason = window.prompt(
      `⚠️ Delete Debit Note ${docNo}\n\nThis action will permanently block this document number.\nPlease provide a reason for deletion:`
    );
    
    if (!reason || reason.trim() === "") {
      if (reason !== null) {
        showMessageBox("Deletion reason is required.", "error");
      }
      return;
    }

    if (!window.confirm(`Are you sure you want to delete Debit Note ${docNo}?\n\nReason: ${reason}`)) {
      return;
    }

    try {
      await arService.deleteDebitNote(id, reason.trim());
      showMessageBox("Debit Note deleted successfully!", "success");
      fetchDebitNotes();
    } catch (error) {
      console.error("Error deleting debit note:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to delete debit note.";
      showMessageBox(`Error: ${errorMessage}`, "error");
    }
  };

  const handleApprove = async (id, docNo) => {
    if (!window.confirm(`Are you sure you want to approve Debit Note ${docNo}?`)) {
      return;
    }

    try {
      await arService.approveDebitNote(id);
      showMessageBox("Debit Note approved successfully!", "success");
      fetchDebitNotes();
    } catch (error) {
      console.error("Error approving debit note:", error);
      showMessageBox("Failed to approve debit note. Please try again.", "error");
    }
  };

  const handleReject = async (id, docNo) => {
    const reason = window.prompt(`Enter reason for rejecting Debit Note ${docNo}:`);
    if (!reason) return;

    try {
      await arService.rejectDebitNote(id, reason);
      showMessageBox("Debit Note rejected successfully!", "success");
      fetchDebitNotes();
    } catch (error) {
      console.error("Error rejecting debit note:", error);
      showMessageBox("Failed to reject debit note. Please try again.", "error");
    }
  };

  const handleSendForApproval = async (id) => {
    try {
      await arService.sendDebitNoteForApproval(id);
      showMessageBox("Debit Note sent for approval successfully!", "success");
      fetchDebitNotes(); // Refresh list
    } catch (error) {
      console.error("Error sending debit note for approval:", error);
      showMessageBox(error.message || "Failed to send for approval. Please ensure an approver is selected.", "error");
    }
  };

  const handlePrint = (id) => {
    navigate(`/ar/debit-notes/${id}/print`);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading debit notes...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="debit-note-list-container">
        <ErrorState
          title="Could not load debit notes"
          description={loadError}
          onRetry={fetchDebitNotes}
        />
      </div>
    );
  }

  return (
    <div className="debit-note-list-container">
      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Doc No, Customer, Site, or Reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="list-info">
          <span>Total: {filteredData.length} debit notes</span>
        </div>
      </div>

      <div className="table-container">
        {filteredData.length === 0 ? (
          <EmptyState
            title="No debit notes"
            description={
              searchTerm
                ? "No debit notes found matching your search."
                : "Debit notes will appear here once they are created."
            }
          />
        ) : (
        <table className="debit-note-table">
          <thead>
            <tr>
              <th>Doc No</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Reason</th>
              <th className="text-right">Amount</th>
              <th>Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
              {filteredData.map((dn) => (
                <tr key={dn.id}>
                  <td>
                    <strong>{dn.doc_no || "N/A"}</strong>
                  </td>
                  <td>{formatDate(dn.debit_note_date)}</td>
                  <td>{dn.customer_name || "N/A"}</td>
                  <td className="reason-cell">{dn.reason || "N/A"}</td>
                  <td className="text-right">{formatCurrency(dn.total_amount)}</td>
                  <td>{getStatusBadge(dn.status, dn.approval_status, dn.is_draft)}</td>
                  <td className="text-center">
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn-action btn-view"
                        onClick={() => handleView(dn.id)}
                        title="View Details"
                      >
                        <IoEyeOutline size={16} />
                      </button>
                      {canEdit && (dn.is_draft || dn.approval_status === "Rejected") && (
                        <>
                          <button
                            type="button"
                            className="btn-action btn-edit"
                            onClick={() => handleEdit(dn.id)}
                            title="Edit"
                          >
                            <IoCreateOutline size={16} />
                          </button>
                          {dn.is_draft && (
                            <button
                              type="button"
                              className="btn-action btn-send"
                              onClick={() => handleSendForApproval(dn.id)}
                              title="Send for Approval"
                            >
                              <IoSendOutline size={16} />
                            </button>
                          )}
                        </>
                      )}
                      {canApprove &&
                        (dn.approval_status === "Pending" ||
                          dn.approval_status === "Pending Approval") && (
                        <>
                          <button
                            type="button"
                            className="btn-action btn-approve"
                            onClick={() => handleApprove(dn.id, dn.doc_no)}
                            title="Approve"
                          >
                            <IoCheckmarkOutline size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn-action btn-reject"
                            onClick={() => handleReject(dn.id, dn.doc_no)}
                            title="Reject"
                          >
                            <IoCloseOutline size={16} />
                          </button>
                        </>
                      )}
                      {dn.approval_status === "Approved" && (
                        <button
                          type="button"
                          className="btn-action btn-print"
                          onClick={() => handlePrint(dn.id, dn.doc_no)}
                          title="Print PDF"
                        >
                          <IoPrintOutline size={16} />
                        </button>
                      )}
                      {canDelete && canDeleteDocument(dn) && (
                        <button
                          type="button"
                          className="btn-action btn-delete"
                          onClick={() => handleDelete(dn.id, dn.doc_no)}
                          title="Delete"
                        >
                          <IoTrashOutline size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
};

export default DebitNoteList;
